import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { serviceurl } from 'src/app/constant/constant';
import { DataService } from 'src/app/services/data.service';
import { ToastService } from 'src/app/services/toast.service';

/** Map packs (maps roadmap Track C): apply the packs you own to your own view
 * of every map, and build packs to sell in the store. */
@Component({
  selector: 'app-map-packs',
  templateUrl: './map-packs.component.html',
  styleUrls: ['./map-packs.component.scss'],
  standalone: false,
})
export class MapPacksComponent implements OnInit {
  readonly lib = 'assets/markers/v1/';
  readonly serviceurl = serviceurl;
  activePath = '';
  loading = true;
  owned: any[] = [];
  made: any[] = [];
  reviewQueue: any[] | null = null; // null: not staff

  // new pack form
  showNew = false;
  newPack = { name: '', description: '', isFree: true, price: '' };

  // builder
  pack: any = null;
  slotGroups: { category: string; slots: any[] }[] = [];
  edit = { name: '', description: '', isFree: true, price: '' };
  newType = { key: '', name: '', category: 'lairs' };
  variantFor: Record<string, string> = {}; // type key -> "size=large, port=true"
  busy = false;
  readonly categoryNames: Record<string, string> = {
    settlements: 'Settlements', encounters: 'Encounters and sites', resources: 'Resources',
    natural: 'Natural features', infrastructure: 'Infrastructure', military: 'Fortifications and military',
    religious: 'Religious and cultural sites', hazards: 'Travel hazards', lairs: 'Creature lairs',
    arcane: 'Arcane and genre sites', factions: 'Factions and heraldry', moving: 'Moving tokens',
  };
  readonly statusText: Record<string, string> = {
    draft: 'Draft', in_review: 'Waiting for review', approved: 'Published', rejected: 'Needs changes',
  };

  constructor(
    private data: DataService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    try {
      this.activePath = localStorage.getItem('activePath') || '';
    } catch {
      /* storage blocked */
    }
    this.route.paramMap.subscribe((p) => {
      const slug = p.get('slug');
      this.refresh();
      if (slug) this.openBuilder(slug);
      else this.pack = null;
    });
    this.data.getMapPackReviewQueue().subscribe({
      next: (q: any) => { this.reviewQueue = q?.staff ? q.packs || [] : null; this.cdr.detectChanges(); },
      error: () => { this.reviewQueue = null; },
    });
  }

  private note(message: string, kind: 'success' | 'warning' | 'note' = 'note') {
    this.toast.showNote(message, kind);
  }

  private fail(err: any, fallback: string) {
    this.busy = false;
    this.note(err?.error?.detail || fallback, 'warning');
    this.cdr.detectChanges();
  }

  refresh(): void {
    this.data.getMapPacks().subscribe({
      next: (r: any) => {
        this.owned = r?.owned || [];
        this.made = r?.made || [];
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (e) => { this.loading = false; this.fail(e, 'Could not load your map packs.'); },
    });
  }

  // --- applying ----------------------------------------------------------------------

  get applied(): any[] {
    return this.owned.filter((p) => p.applied).sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }

  toggleApply(p: any): void {
    const call = p.applied ? this.data.unapplyMapPack(p.slug) : this.data.applyMapPack(p.slug, this.applied.length);
    call.subscribe({
      next: () => { this.note(p.applied ? 'Pack removed from your maps.' : 'Pack applied to your maps.', 'success'); this.refresh(); },
      error: (e) => this.fail(e, 'Could not change the pack.'),
    });
  }

  /** Swap a pack with its neighbour in the applied order (first wins). */
  move(p: any, step: number): void {
    const order = this.applied;
    const i = order.findIndex((x) => x.slug === p.slug);
    const j = i + step;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    let pending = order.length;
    order.forEach((x, n) => this.data.applyMapPack(x.slug, n).subscribe({
      next: () => { if (--pending === 0) this.refresh(); },
      error: (e) => this.fail(e, 'Could not reorder the packs.'),
    }));
  }

  // --- making --------------------------------------------------------------------------

  createPack(): void {
    if (!this.newPack.name.trim()) return;
    this.busy = true;
    this.data.createMapPack({
      name: this.newPack.name, description: this.newPack.description,
      is_free: this.newPack.isFree, price: this.newPack.isFree ? null : this.newPack.price,
    }).subscribe({
      next: (p: any) => {
        this.busy = false;
        this.showNew = false;
        this.newPack = { name: '', description: '', isFree: true, price: '' };
        this.router.navigate(['/map-packs', p.slug]);
      },
      error: (e) => this.fail(e, 'Could not create the pack.'),
    });
  }

  openBuilder(slug: string): void {
    this.data.getMapPack(slug).subscribe({
      next: (p: any) => {
        this.pack = p;
        this.edit = { name: p.name, description: p.description, isFree: p.is_free, price: p.price || '' };
        const groups: Record<string, any[]> = {};
        for (const s of p.slots || []) (groups[s.category] ||= []).push(s);
        this.slotGroups = Object.keys(this.categoryNames)
          .filter((c) => groups[c]?.length)
          .map((c) => ({ category: c, slots: groups[c] }));
        this.cdr.detectChanges();
      },
      error: (e) => this.fail(e, 'Could not open the pack.'),
    });
  }

  saveDetails(): void {
    this.busy = true;
    this.data.updateMapPack(this.pack.slug, {
      name: this.edit.name, description: this.edit.description,
      is_free: this.edit.isFree, price: this.edit.isFree ? null : this.edit.price,
    }).subscribe({
      next: () => { this.busy = false; this.note('Pack saved.', 'success'); this.openBuilder(this.pack.slug); this.refresh(); },
      error: (e) => this.fail(e, 'Could not save the pack.'),
    });
  }

  /** "size=large, port=true" -> {size: 'large', port: true} */
  private parseVariant(text: string): Record<string, any> {
    const out: Record<string, any> = {};
    for (const part of (text || '').split(',')) {
      const [k, v] = part.split('=').map((s) => (s || '').trim());
      if (k) out[k] = v === 'true' ? true : v === 'false' ? false : v;
    }
    return out;
  }

  variantLabel(v: Record<string, any>): string {
    const parts = Object.entries(v || {}).map(([k, val]) => `${k} ${val}`);
    return parts.length ? parts.join(', ') : 'Default';
  }

  upload(slot: any, event: Event, withVariant = false): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const variant = withVariant ? this.parseVariant(this.variantFor[slot.type]) : {};
    this.busy = true;
    this.data.uploadMapPackArt(this.pack.slug, slot.type, file, variant).subscribe({
      next: () => { this.busy = false; this.note('Picture added.', 'success'); this.openBuilder(this.pack.slug); },
      error: (e) => this.fail(e, 'Could not add the picture.'),
    });
  }

  removeArt(art: any): void {
    this.data.deleteMapPackArt(this.pack.slug, art.id).subscribe({
      next: () => this.openBuilder(this.pack.slug),
      error: (e) => this.fail(e, 'Could not remove the picture.'),
    });
  }

  addType(): void {
    if (!this.newType.key.trim()) return;
    this.data.addMapPackType(this.pack.slug, this.newType).subscribe({
      next: () => { this.newType = { key: '', name: '', category: this.newType.category }; this.openBuilder(this.pack.slug); },
      error: (e) => this.fail(e, 'Could not add the marker type.'),
    });
  }

  importZip(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    this.busy = true;
    this.data.importMapPackZip(this.pack.slug, file).subscribe({
      next: (r: any) => {
        this.busy = false;
        const errs = (r?.errors || []).length;
        this.note(`${r?.added || 0} pictures added` + (errs ? `, ${errs} refused: ${r.errors[0]}` : '.'), errs ? 'warning' : 'success');
        this.openBuilder(this.pack.slug);
      },
      error: (e) => this.fail(e, 'Could not import the zip.'),
    });
  }

  exportZip(): void {
    this.data.exportMapPackZip(this.pack.slug).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = this.pack.slug + '.zip';
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      },
      error: (e) => this.fail(e, 'Could not export the pack.'),
    });
  }

  submit(): void {
    this.data.submitMapPack(this.pack.slug).subscribe({
      next: (p: any) => {
        this.note(p.status === 'approved' ? 'Pack published.' : 'Sent for review.', 'success');
        this.openBuilder(this.pack.slug);
        this.refresh();
      },
      error: (e) => this.fail(e, 'Could not submit the pack.'),
    });
  }

  deletePack(): void {
    this.data.deleteMapPack(this.pack.slug).subscribe({
      next: () => { this.note('Pack deleted.', 'success'); this.router.navigate(['/map-packs']); },
      error: (e) => this.fail(e, 'Could not delete the pack.'),
    });
  }

  confirmDelete = false;

  decide(p: any, approve: boolean, note: string): void {
    this.data.reviewMapPack(p.slug, approve, note).subscribe({
      next: () => {
        this.reviewQueue = (this.reviewQueue || []).filter((x) => x.slug !== p.slug);
        this.note(approve ? 'Pack approved.' : 'Sent back to the creator.', 'success');
        this.cdr.detectChanges();
      },
      error: (e) => this.fail(e, 'Could not record the review.'),
    });
  }
}
